import type { NavigationGuard, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { UserType } from '@/enums/auth'
import { isDevFullMenu } from '@/utils/app-config'
import { REPORT_VIEW_CODE, type ReportViewCode } from '../types'

export const REPORT_CENTER_PERMISSION = 'module:report-center:report-center'
const REPORT_VIEW_PERMISSIONS: Record<ReportViewCode, string> = {
  MANAGEMENT: 'role:report-center:management-view',
  FOLLOW_UP: 'role:report-center:follow-up-view'
}

/** 返回当前用户获准使用的视角，菜单准入始终独立于租户管理员角色。 */
export function getAccessibleReportViews(): ReportViewCode[] {
  const views = Object.values(REPORT_VIEW_CODE)
  if (isDevFullMenu()) return views

  const authStore = useAuthStore()
  if (!authStore.user || !authStore.hasExplicitMenuPermission(REPORT_CENTER_PERMISSION)) return []
  if (authStore.user.userType === UserType.TENANT) {
    if (!authStore.hasPermission(REPORT_CENTER_PERMISSION)) return []
    return views.filter(view => authStore.hasPermission(REPORT_VIEW_PERMISSIONS[view]))
  }
  // 客户端授权由 menuPrivilege 返回，客户管理员不得绕过企业分配的视角权限。
  if (authStore.user.userType === UserType.CLIENT) {
    return views.filter(view => authStore.hasExplicitMenuPermission(REPORT_VIEW_PERMISSIONS[view]))
  }
  return []
}

/** 无视角时隐藏报告入口，保留路由以便直接访问时给出无权限提示。 */
export function applyReportCenterRouteVisibility(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  return routes.map(route => ({
    ...route,
    ...(route.name === 'ReportCenter'
      ? { meta: { ...route.meta, hidden: getAccessibleReportViews().length === 0 } }
      : {}),
    ...(route.children ? { children: applyReportCenterRouteVisibility(route.children) } : {})
  })) as RouteRecordRaw[]
}

/** 新旧地址共用报告入口和至少一个视角的权限要求。 */
export const canEnterReportCenter: NavigationGuard = () =>
  getAccessibleReportViews().length > 0 ? true : { path: '/no-permission', replace: true }
